<template>
  <loader :is-loading="loading" :fullPage="true" color="primary">
    <v-container fluid class="align-start align-content-start fill-height">
      <div class="bs-table-header">
        <v-row class="mb-1">
          <v-col cols="12" class="col-without-bottom-padding px-8 mt-6 mb-3">
            <h1 class="bs-header-title">
              {{ $t(`components.tripsHistory.title`) }}
            </h1>
          </v-col>
          <v-row justify="space-between" class="align-end mb-3 px-8">
            <v-col md="6" class="col-without-top-padding col-without-bottom-padding v-tabs-container">
              <v-tabs
                v-model="tab"
                hide-slider
              >
                <v-tab
                  v-for="item in tabs"
                  :key="item.tab"
                  :ripple="false"
                  class="tabs-text"
                  active-class="tabs-text-active"
                  @click="$router.push(`/trips/${item.tab}`)"
                >
                  {{ $t(`components.tripsHistory.tabs.${item.tab}.title`) }}
                </v-tab>
              </v-tabs>
            </v-col>
            <v-col sm="5" md="6" lg="6" xl="5" class="d-flex align-center col-without-top-padding col-without-bottom-padding">
              <v-text-field
                v-model="dispatchId"
                :label="$t('components.tripsHistory.tripId')"
                color="primary"
                prepend-inner-icon="mdi-magnify"
                clearable
                outlined
                hide-details
                :height="38"
                class="mr-2 bs-search-item"
              >
              </v-text-field>
              <v-btn
                color="white"
                class="ma-2 search-passenger-btn"
                :height="40"
                depressed
                @click="btnSearchPassengerClick"
              >
                <v-icon left>
                  mdi-account-search
                </v-icon>
                <span v-if="!selectedPassenger">{{ $t('components.tripsHistory.passenger') }}</span>
                <span v-if="selectedPassenger">{{ $t('components.tripsHistory.passenger') }}:
                  {{selectedPassenger.givenName}} {{selectedPassenger.familyName}}
                  <v-icon small>mdi-close</v-icon>
                </span>
              </v-btn>
              <v-btn v-if="hasAccess(CORP_ADMIN)" to="/book" class="add-bs-button" :height="40" color="#333333" depressed>
                <v-icon left>mdi-plus</v-icon>
                {{ $t('components.tripsHistory.add') }}
              </v-btn>
              <v-menu
                left
                offset-y
                nudge-bottom="15"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    dark
                    icon
                    v-bind="attrs"
                    v-on="on"
                    class="icon-button-color"
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="showDownloadModal">
                    <v-list-item-title class="flex justify-space-between">
                      <span class="mr-3">{{ $t('components.tripsHistory.download') }}</span>
                      <v-icon>mdi-tray-arrow-down</v-icon>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>
        </v-row>
      </div>
      <div class="bs-table-table">
        <v-row no-gutters>
          <v-col class="col-without-top-padding table-color">
            <v-data-table
              class="bs-table elevation-0"
              calculate-widths
              :items="trips"
              :headers="headers"
              :server-items-length="totalTrips"
              :loading="corporationsLoading"
              :multi-sort="false"
              :must-sort="true"
              sort-by="pickupTime"
              :sort-desc="true"
              :single-expand="true"
              :expanded.sync="expanded"
              :page.sync="page"
              :options.sync="options"
              :items-per-page="itemsPerPage"
              hide-default-footer
              @page-count="pageCount = $event"
              @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
            >

              <template v-slot:expanded-item="{ headers, item }">
                <expand-item
                  :headers="headers"
                  :data="item"
                  :tab="getTabFilter()"
                />
              </template>

              <template v-slot:item.createdAt="{ item: { pickupTime } }">
                {{ handlePickupTimeDisplay(pickupTime, true) }}
              </template>

              <template v-slot:item.pickupTime="{ item: { pickupTime } }">
                {{ handlePickupTimeDisplay(pickupTime)  }}
              </template>

              <template v-slot:item.name="{ item }">
                {{ getFullName(item) }}
              </template>

              <template v-slot:item.from="{ item: { origin } }">
                <span class="text-overflow">{{ origin.address }}</span>
              </template>

              <template v-slot:item.to="{ item: { destination } }">
                <span class="text-overflow">{{ destination.address }}</span>
              </template>

              <template v-slot:item.pricing="{ item }">
                <span v-if="item.pricing">
                  {{ currencySymbol + '' + item.pricing.totalPrice }}
                </span>
              </template>

              <template v-slot:item.status="{ item: { status } }">
                <div :class="`status-container py-1 px-2 status-${status}`">
                  {{ status === 'DROPPINGOFF' ? 'POB': status }}
                </div>
              </template>
            </v-data-table>
            <div class="d-flex justify-between align-center py-2 px-4 bs-table-footer">
              <v-row no-gutters class="bs-per-page">
                <span>
                  {{ $t(`components.tripsHistory.perPage`) }}
                </span>
                <span
                  v-for="item  in [10, 20, 30]"
                  :key="item"
                  class="px-1"
                  :class="{ 'active-page-count': item === options.itemsPerPage }"
                  @click="options.itemsPerPage = item"
                >
                  {{ item }}
                </span>
              </v-row>
              <v-pagination
                v-model="page"
                :length="pageCount"
                :total-visible="7"
                color="#f2f2f2"
                class="bs-pagination"
              ></v-pagination>
            </div>
          </v-col>
        </v-row>
      </div>
      <passenger-search-dialog
        v-if="getSelectedCorporationId"
        :showDialog="showDialog"
        :businessAccountId="getSelectedCorporationId"
        @closeClick="showDialog = false"
        @passengerSelected="passengerSelected"
      >
      </passenger-search-dialog>
    </v-container>
  </loader>
</template>

<script>
import { mapGetters } from 'vuex';
import { getRoles } from '@/mixins/authentication.mixins';
import { bookingChannelComputed } from '@/mixins/bookingChannel.mixins';
import { longDateTimeDay, time } from '@/helpers/dateTime';
import { CHANNEL_ADMIN, CORP_ADMIN } from '@/helpers/userRoles';
import PassengerSearchDialog from '@/components/tripsHistory/dialogs/PassengerSearchDialog.vue';
import ExpandItem from './ExpandItem.vue';

export default {
  components: { ExpandItem, PassengerSearchDialog },
  mixins: [bookingChannelComputed, getRoles],
  props: {
    totalTrips: {
      type: Number,
      required: true,
    },
    trips: {
      type: Array,
      required: true,
    },
    corporationsLoading: {
      type: Boolean,
      required: true,
    },
  },
  filters: { longDateTimeDay, time },
  data() {
    return {
      timer: null,
      CHANNEL_ADMIN,
      CORP_ADMIN,
      tabs: [
        { tab: 'all-trips', filter: ['current', 'past', 'future', 'cancelled'] },
        { tab: 'in-progress', filter: ['current'] },
        { tab: 'pre-bookings', filter: ['future'] },
        { tab: 'completed', filter: ['past'] },
        { tab: 'cancelled', filter: ['cancelled'] },
      ],
      loading: false,
      tab: 1,
      page: 1,
      pageCount: 0,
      options: {},
      expanded: [],
      itemsPerPage: 10,
      dispatchId: null,
      showDialog: false,
      selectedPassenger: null,
    };
  },
  mounted() {
    if (this.$route.params.tab) {
      const routeTab = this.$route.params.tab;
      const tab = this.tabs.filter((val) => val.tab === routeTab);
      this.tab = this.tabs.indexOf(tab[0]);
    }
    this.time = setInterval(() => {
      this.loadData();
    }, 10000);
  },
  watch: {
    options: {
      handler() {
        this.loadData();
      },
      deep: true,
    },
    tab: {
      handler() {
        this.options.page = 1;
        this.loadData();
      },
    },
    dispatchId: {
      handler() {
        this.options.page = 1;
        this.loadData();
      },
    },
    getSelectedCorporationId: {
      handler() {
        this.loadData();
      },
      immediate: true,
    },
    $route: {
      handler(val) {
        this.tab = val.meta.tab;
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  computed: {
    ...mapGetters('corporations', ['getSelectedCorporationId']),
    headers() {
      return [
        {
          text: this.$t('components.tripsHistory.list.date'),
          align: 'left',
          sortable: true,
          value: 'createdAt',
          width: '12%',
        },
        {
          text: this.$t('components.tripsHistory.list.time'),
          align: 'left',
          sortable: true,
          value: 'pickupTime',
          width: '8%',
        },
        {
          text: this.$t('components.tripsHistory.list.name'),
          align: 'left',
          sortable: false,
          value: 'name',
          width: '12%',
        },
        {
          text: this.$t('components.tripsHistory.list.fleetName'),
          align: 'left',
          sortable: false,
          value: 'companyName',
          width: '10%',
        },
        {
          text: this.$t('components.tripsHistory.list.from'),
          align: 'left',
          sortable: false,
          value: 'from',
        },
        {
          text: this.$t('components.tripsHistory.list.to'),
          align: 'left',
          sortable: false,
          value: 'to',
        },
        {
          text: this.$t('components.tripsHistory.list.amount'),
          align: 'center',
          sortable: false,
          value: 'pricing',
          width: '9%',
        },
        {
          align: 'center',
          sortable: false,
          value: 'status',
        },
      ];
    },
  },
  methods: {
    showDownloadModal() {
      this.$emit('showDownloadModal');
    },
    handlePickupTimeDisplay(pickupTime, showDate) {
      if (showDate) {
        return pickupTime ? longDateTimeDay(pickupTime) : 'N/A';
      }
      return pickupTime ? time(pickupTime) : 'N/A';
    },
    getFullName(item) {
      return `${item.userInfo.givenName} ${item.userInfo.familyName}`;
    },
    getTabFilter() {
      return this.tabs[this.tab].filter;
    },
    async loadData() {
      if (!this.getSelectedCorporationId) return;

      const {
        page, itemsPerPage, sortBy, sortDesc,
      } = this.options;
      const sort = sortBy && sortBy[0];
      this.$emit('callLoadData', {
        page: page - 1,
        maxPerPage: itemsPerPage,
        dispatchId: this.dispatchId,
        sort: sortDesc[0] ? `-${sort}` : sort,
        filter: this.getTabFilter(),
        corporationId: this.getSelectedCorporationId,
        passengerId: (this.selectedPassenger) ? this.selectedPassenger.id : null,
      });
    },
    passengerSelected(passenger) {
      this.selectedPassenger = passenger;
      this.loadData();
      this.showDialog = false;
    },
    btnSearchPassengerClick() {
      if (this.selectedPassenger) {
        this.selectedPassenger = null;
        this.loadData();
        return;
      }
      this.showDialog = true;
    },
  },
};
</script>
<style scoped>
.icon-button-color {
  color: #b7b7b7 !important;
}
.v-list-item__title {
  font-size: 0.87rem !important;
}

.v-list {
   padding: 0 !important;
}
.v-list-item__title, .v-list-item__subtitle {
   text-overflow: inherit !important;
}
</style>
